import 'react-loading-skeleton/dist/skeleton.css';

import { CONSTANTS, HELPERS } from '@/utils';
import { 
  getPurchaseState, 
  calculatePrice, 
  getBannerConfig,
  shouldEnableMentoringToggle,
  getInitialMentoringState
} from '@/utils/assessmentLogic';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ACTION_PAYMENT } from '@/redux/actions';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms';
import CourseDetailModal from '@/components/molecules/CourseDetailModal';
import { Icon } from '@iconify/react';
import MentoringCard from './MentoringCard';
import PurchaseBanner from './PurchaseBanner';
import PATH from '@/routes/path';
import ReactPixel from 'react-facebook-pixel';
import { SERVICE_AI_ASSESSMENT } from '@/services/aiService';
import Skeleton from 'react-loading-skeleton';
import { notify } from 'react-notify-toast';
import { useAddToCart } from '@/hooks/cart/useCartMutations';
import { useHistory } from 'react-router-dom';

const ProductCard = ({
  product,
  isLast,
  onAddToCart,
  onBuy,
  loadingStates,
  onNavigateToCourse,
  onShowCourseDetail,
}) => {
  const getItemType = (type) => {
    switch (type) {
      case 'consultation':
        return 'event';
      case 'workshop':
        return 'event';
      case 'course':
        return 'course';
      default:
        return 'course';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'consultation':
        return 'Konsultasi';
      case 'workshop':
        return 'Workshop';
      case 'course':
        return 'Kursus';
      default:
        return 'Kursus';
    }
  };

  const formatCurrency = (amount) => {
    const numAmount = Number(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      return 'Rp 0';
    }
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  return (
    <div
      className={`relative flex items-start w-full ${!isLast
        ? "md:after:content-[''] md:after:w-0.5 md:after:h-full md:after:bg-[#e3e2e3] md:after:inline-block md:after:absolute md:after:-bottom-6 md:after:left-4"
        : ''
        }`}
    >
      {/* Timeline Circle */}
      <div className="w-8 h-8 bg-[#f9f8fa] hidden md:block border-2 border-solid border-[#2b2b2b] rounded-2xl z-10 relative mr-4 lg:mr-8 flex-shrink-0" />

      {/* Main Card */}
      <div className="bg-white p-3 sm:p-4 lg:p-6 rounded-lg shadow-sm border border-[#e3e2e3] mb-4 sm:mb-6 w-full max-w-full">
        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4 min-h-[160px]">
          {/* Left Column - Content */}
          <div className="flex flex-col justify-between order-1 lg:order-1">
            <div>
              <div className="flex flex-wrap gap-2 mb-2">
                <Badge itemType={getItemType(product.type)} className="px-3 py-1 rounded-2xl text-sm">
                  {getTypeLabel(product.type)}
                </Badge>
              </div>
              <h3
                className="[font-family:'Raleway',Helvetica] font-bold text-[#191919] text-lg sm:text-xl leading-6 sm:leading-7 mb-2 line-clamp-2 cursor-pointer hover:text-[#8553d8] transition-colors break-words"
                onClick={() => {
                  if (product.type === 'course') {
                    if (product.is_purchased || product.is_owned) {
                      onNavigateToCourse(product);
                    } else {
                      onShowCourseDetail(product);
                    }
                  } else {
                    onNavigateToCourse(product);
                  }
                }}
              >
                {product.title}
              </h3>
            </div>
            <div className="flex items-center gap-1.5 mt-2">
              <span className="[font-family:'Source_Sans_Pro',Helvetica] font-normal text-[#636363] text-sm leading-5 break-words">
                {(() => {
                  const videoCount = product.totalVideos || product.modules || product.videoCount || 0;
                  const duration = product.totalDuration || product.duration || product.estimatedDuration || '';
                  let formattedDuration = '';
                  const durationStr = duration.toString().trim();

                  if (
                    durationStr &&
                    durationStr !== '0' &&
                    durationStr !== '0 menit' &&
                    durationStr !== '0 jam' &&
                    durationStr !== '0.0 menit' &&
                    !durationStr.startsWith('0 ') &&
                    !durationStr.startsWith('0.')
                  ) {
                    if (durationStr.includes('menit') || durationStr.includes('jam')) {
                      formattedDuration = durationStr;
                    } else {
                      formattedDuration = `${durationStr} menit`;
                    }
                  } else {
                    formattedDuration = 'Durasi belum tersedia';
                  }

                  return videoCount > 0 ? `${videoCount} video • ${formattedDuration}` : formattedDuration;
                })()}
              </span>
            </div>
          </div>

          {/* Right Column - Price and Actions */}
          <div className="flex flex-col justify-between order-2 lg:order-2 lg:text-right">

            {(!product.is_purchased && !product.is_owned && product.can_purchase) &&
              !(product.is_subscription_access && !product.is_direct_purchase) ? (
              <>
                <div>
                  <div className="flex flex-col sm:flex-row lg:justify-end lg:items-center gap-2 mb-2">
                    <div className="flex items-center gap-2 lg:justify-end">
                      {Number(product.discount) > 0 && (
                        <Badge variant="discount">Disc {Math.round(Number(product.discount))}%</Badge>
                      )}
                      {Number(product.originalPrice) > 0 &&
                        Number(product.originalPrice) > Number(product.price) && (
                          <p className="text-xs text-gray-400 line-through">
                            {formatCurrency(Number(product.originalPrice))}
                          </p>
                        )}
                    </div>
                  </div>
                  {/* Current price */}
                  <p className="[font-family:'Inter',Helvetica] font-bold text-[#7344BE] text-lg leading-7 mb-3 lg:text-right">
                    {Number(product.price) > 0 ? formatCurrency(Number(product.price)) : 'Harga belum tersedia'}
                  </p>
                </div>
                {/* Buttons */}
                <div className="flex flex-col sm:flex-row lg:justify-end items-stretch sm:items-center gap-2">
                  <Button
                    variant={Button.VARIANTS.GRAY_OUTLINE}
                    size={Button.SIZES.MEDIUM}
                    className="px-3 sm:px-4 py-3 rounded-[40px] [font-family:'Raleway',Helvetica] font-semibold text-[#343338] text-sm leading-4 flex-1 sm:flex-none min-w-0"
                    onClick={() => onAddToCart(product)}
                    disabled={loadingStates.addToCart[product.productId]}
                  >
                    {loadingStates.addToCart[product.productId] ? (
                      <>
                        <span className="sm:hidden">⏳</span>
                        <span className="hidden sm:inline truncate">Menambah...</span>
                      </>
                    ) : (
                      <>
                        <span className="sm:hidden">
                          <Icon icon="material-symbols:shopping-cart-outline" className="w-4 h-4" />
                        </span>
                        <span className="hidden sm:inline truncate">Tambah Keranjang</span>
                      </>
                    )}
                  </Button>
                  <Button
                    variant={Button.VARIANTS.SECONDARY}
                    size={Button.SIZES.MEDIUM}
                    className="px-4 sm:px-8 py-3 bg-[#05d172] rounded-[40px] font-semibold text-sm leading-4 flex-1 sm:flex-none min-w-0"
                    onClick={() => onBuy(product)}
                    disabled={loadingStates.buyNow[product.productId]}
                  >
                    <span className="truncate">
                      {loadingStates.buyNow[product.productId] ? 'Memproses...' : 'Beli'}
                    </span>
                  </Button>
                </div>
              </>
            ) : (
              /* Show "Belajar Sekarang" for owned/purchased items and subscription access */
              <div className="flex flex-col items-start lg:items-end justify-center h-full">
                <Button
                  variant={Button.VARIANTS.SECONDARY}
                  size={Button.SIZES.MEDIUM}
                  className="px-4 sm:px-8 py-3 bg-[#8553d8] rounded-[40px] font-semibold text-sm leading-4 w-full sm:w-auto min-w-0"
                  onClick={() => {
                    if (product.type === 'course') {
                      onNavigateToCourse(product);
                    } else {
                      onNavigateToCourse(product);
                    }
                  }}
                >
                  <span className="truncate">Belajar Sekarang</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


const AssessmentBanner = ({ canCreateNew, onGenerateNew, isGenerating }) => {
  if (canCreateNew) {
    return (
      <div className="flex flex-col gap-4 sm:gap-6 px-4 sm:px-8 py-4 w-full bg-[#f0eff0] rounded-2xl mb-6">
        <div className="flex items-center gap-4">
          <Icon icon="material-symbols:auto-awesome" className="w-6 h-6 text-[#8553d8] flex-shrink-0" />
          <h3 className="[font-family:'Raleway',Helvetica] font-medium text-[#231838] text-lg sm:text-xl leading-7">
            Generate Quiz Baru
          </h3>
        </div>
        <div className="flex flex-col gap-3">
          <p className="text-[#636363] text-sm break-words">
            Sudah siap untuk mendapatkan rekomendasi program belajar yang lebih update? Generate quiz baru
            untuk hasil yang lebih personal!
          </p>
        </div>
        <div className="flex justify-end">
          <Button
            variant={Button.VARIANTS.SECONDARY}
            className="px-4 sm:px-6 py-3.5 rounded-[40px] [font-family:'Raleway',Helvetica] font-semibold text-white text-sm sm:text-base leading-5 min-w-0"
            onClick={onGenerateNew}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <div className="flex items-center gap-2">
                <Icon icon="eos-icons:loading" className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Generating...</span>
              </div>
            ) : (
              <span className="truncate">Generate Quiz Baru</span>
            )}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 sm:px-8 py-4 w-full bg-[#f0eff0] rounded-2xl mb-6">
      <div className="flex items-start gap-4">
        <Icon icon="material-symbols:schedule" className="w-6 h-6 text-[#636363] flex-shrink-0 mt-1" />
        <div className="min-w-0">
          <h3 className="[font-family:'Raleway',Helvetica] font-medium text-[#231838] text-lg sm:text-xl leading-7 break-words">
            Quiz Sudah Selesai
          </h3>
          <p className="text-[#636363] text-sm mt-1 break-words">
            Quiz hanya dapat dilakukan 1 kali per bulan. Quiz berikutnya tersedia bulan depan.
          </p>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          linkTo={PATH.ASSESSMENT_HISTORY}
          variant={Button.VARIANTS.SECONDARY}
          className="px-4 sm:px-6 py-3.5 rounded-[40px] [font-family:'Raleway',Helvetica] font-semibold text-white text-sm sm:text-base leading-5 min-w-0"
        >
          <span className="truncate">Jelajahi Program</span>
        </Button>
      </div>
    </div>
  );
};

const FeedbackComponent = ({ assessmentId, existingFeedback, onFeedbackSubmitted }) => {
  const [rating, setRating] = React.useState(0);
  const [hoveredRating, setHoveredRating] = React.useState(0);
  const [feedback, setFeedback] = React.useState('');
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  if (existingFeedback?.has_feedback) {
    return (
      <div className="flex flex-col gap-4 px-4 sm:px-8 py-4 w-full bg-[#f0eff0] rounded-2xl">
        <div className="flex items-center gap-4">
          <Icon icon="material-symbols:check-circle" className="w-6 h-6 text-green-600 flex-shrink-0" />
          <h3 className="[font-family:'Raleway',Helvetica] font-medium text-[#231838] text-lg sm:text-xl leading-7">
            Feedback Anda
          </h3>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="text-[#636363] text-sm font-medium">Rating:</span>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <Icon
                key={star}
                icon="material-symbols:star"
                className={`w-5 h-5 ${star <= existingFeedback.rating ? 'text-yellow-400' : 'text-gray-300'}`}
              />
            ))}
            <span className="ml-2 text-sm text-[#636363] font-medium">{existingFeedback.rating}/5</span>
          </div>
        </div>
        {existingFeedback.feedback_text && (
          <div className="flex flex-col gap-2">
            <span className="text-[#636363] text-sm font-medium">Komentar:</span>
            <p className="text-[#231838] text-sm bg-white p-3 rounded-lg border break-words">
              {existingFeedback.feedback_text}
            </p>
          </div>
        )}
        <div className="text-xs text-[#636363] text-right">
          Dikirim pada:{' '}
          {new Date(existingFeedback.submitted_at).toLocaleDateString('id-ID', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>
    );
  }

  const handleSubmitFeedback = async () => {
    if (rating === 0) {
      notify.show('Mohon berikan rating terlebih dahulu', 'warning');
      return;
    }
    if (!assessmentId) {
      notify.show('Quiz ID tidak tersedia', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await SERVICE_AI_ASSESSMENT.submitAssessmentFeedback(
        assessmentId,
        rating,
        feedback.trim(),
        {
          session_duration: Math.floor((Date.now() - performance.now()) / 1000),
        }
      );

      if (response.success) {
        setIsSubmitted(true);
        notify.show('Terima kasih atas feedback Anda!', 'success');
        HELPERS.sendEventGA('assessment_feedback', {
          rating: rating,
          has_comment: feedback.trim().length > 0,
          page_source: 'Assessment Results',
          assessment_id: assessmentId,
        });
        if (onFeedbackSubmitted) {
          onFeedbackSubmitted();
        }
      } else {
        throw new Error(response.error || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Feedback submission error:', error);
      if (error.response?.error?.code) {
        const errorCode = error.response.error.code;
        switch (errorCode) {
          case 'FEEDBACK_ALREADY_EXISTS':
            notify.show('Anda sudah memberikan feedback untuk quiz ini', 'warning');
            break;
          case 'ASSESSMENT_NOT_FOUND':
            notify.show('Quiz tidak ditemukan', 'error');
            break;
          case 'VALIDATION_ERROR':
            notify.show('Data yang dikirim tidak valid', 'error');
            break;
          default:
            notify.show(error.response.error.message || 'Gagal mengirim feedback. Silakan coba lagi.', 'error');
        }
      } else {
        notify.show('Gagal mengirim feedback. Silakan coba lagi.', 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 sm:px-8 py-4 w-full bg-[#f0eff0] rounded-2xl">
        <div className="flex items-center gap-4">
          <Icon icon="material-symbols:check-circle" className="w-6 h-6 text-green-600 flex-shrink-0" />
          <h3 className="[font-family:'Raleway',Helvetica] font-medium text-[#231838] text-lg sm:text-xl leading-7">
            Terima kasih atas feedback Anda!
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-6 px-4 sm:px-8 py-4 w-full bg-[#f0eff0] rounded-2xl">
      <div className="flex items-center gap-4">
        <Icon icon="material-symbols:feedback" className="w-6 h-6 text-[#231838] flex-shrink-0" />
        <h3 className="[font-family:'Raleway',Helvetica] font-medium text-[#231838] text-lg sm:text-xl leading-7">
          Bagaimana rekomendasi program belajar ini?
        </h3>
      </div>
      <div className="flex flex-col gap-3">
        <p className="text-[#636363] text-sm leading-5 break-words">
          Berikan rating untuk membantu kami memberikan rekomendasi yang lebih baik
        </p>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-2">
          <div className="flex items-center gap-1 sm:gap-2 justify-center sm:justify-start">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className="transition-all duration-200 hover:scale-110 p-1"
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                onClick={() => setRating(star)}
              >
                <Icon
                  icon={
                    star <= (hoveredRating || rating) ? 'material-symbols:star' : 'material-symbols:star-outline'
                  }
                  className={`w-7 sm:w-8 h-7 sm:h-8 transition-colors duration-200 ${star <= (hoveredRating || rating) ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-200'
                    }`}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <span className="text-center sm:text-left sm:ml-3 text-sm text-[#636363] font-medium">
              {rating === 1 && 'Kurang sesuai'}
              {rating === 2 && 'Cukup sesuai'}
              {rating === 3 && 'Sesuai'}
              {rating === 4 && 'Sangat sesuai'}
              {rating === 5 && 'Luar biasa!'}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <label className="text-sm font-medium text-[#231838]">Komentar tambahan (opsional)</label>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Bagikan pengalaman Anda tentang hasil rekomendasi ini..."
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8553d8] focus:border-[#8553d8] resize-none transition-colors text-sm"
          rows="3"
          maxLength={500}
        />
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500">{feedback.length}/500 karakter</span>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          variant={Button.VARIANTS.GRAY_OUTLINE}
          className="px-4 sm:px-6 py-3.5 rounded-[40px] border border-solid border-[#231838] [font-family:'Raleway',Helvetica] font-semibold text-[#231838] text-sm sm:text-base leading-5 min-w-0"
          onClick={handleSubmitFeedback}
          disabled={isSubmitting || rating === 0}
        >
          {isSubmitting ? (
            <div className="flex items-center gap-2">
              <Icon icon="eos-icons:loading" className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">Mengirim...</span>
            </div>
          ) : (
            <span className="truncate">Kirim Feedback</span>
          )}
        </Button>
      </div>
    </div>
  );
};

// Skeleton component for loading state
const AssessmentResultsSkeleton = () => {
  return (
    <div className="flex flex-col w-full bg-[#f9f8fa] pb-6 md:pb-12">
      <section className="flex flex-col w-full max-w-[894px] items-center justify-center gap-6 sm:gap-10 px-4 md:px-16 py-8 sm:py-12 mx-auto bg-white rounded-lg shadow-sm my-4 sm:my-8">
        {/* Header Skeleton */}
        <div className="w-full">
          <div className="flex flex-col gap-4 px-4 sm:px-8 py-4 w-full bg-[#f0eff0] rounded-2xl mb-6">
            <div className="flex items-center gap-4">
              <Skeleton circle width={24} height={24} />
              <Skeleton height={24} width="60%" />
            </div>
            <Skeleton height={16} width="90%" />
            <div className="flex justify-end">
              <Skeleton height={40} width={150} className="rounded-full" />
            </div>
          </div>

          {/* Title Section Skeleton */}
          <div className="flex flex-col items-center justify-center gap-2 w-full mb-8">
            <Skeleton height={32} width="80%" className="mb-2" />
            <Skeleton height={16} width="100%" />
            <Skeleton height={16} width="70%" />
          </div>
        </div>

        {/* Products Skeleton */}
        <div className="flex flex-col items-start gap-6 w-full">
          {[1, 2, 3].map((index) => (
            <div key={index} className="relative flex items-start w-full">
              {/* Timeline Circle */}
              <Skeleton circle width={32} height={32} className="mr-4 lg:mr-8 flex-shrink-0" />

              {/* Product Card Skeleton */}
              <div className="bg-white p-3 sm:p-4 lg:p-6 rounded-lg shadow-sm border border-[#e3e2e3] w-full">
                <div className="flex flex-col lg:grid lg:grid-cols-2 gap-4">
                  {/* Left Column */}
                  <div className="flex flex-col justify-between">
                    <div>
                      <Skeleton height={24} width={80} className="mb-2 rounded-full" />
                      <Skeleton height={20} width="90%" className="mb-2" />
                      <Skeleton height={16} width="70%" />
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="flex flex-col justify-between lg:text-right">
                    <div>
                      <div className="flex flex-col sm:flex-row lg:justify-end gap-2 mb-2">
                        <Skeleton height={20} width={60} className="rounded-full" />
                        <Skeleton height={16} width={80} />
                      </div>
                      <Skeleton height={20} width={100} className="mb-3" />
                    </div>
                    <div className="flex gap-2 lg:justify-end">
                      <Skeleton height={40} width={120} className="rounded-full" />
                      <Skeleton height={40} width={80} className="rounded-full" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Bundle Offer Skeleton */}
          <div className="flex flex-col gap-4 p-4 sm:px-6 sm:py-4 w-full bg-[#5d399a] rounded-2xl">
            <div className="flex items-center gap-4">
              <Skeleton circle width={32} height={32} baseColor="#ffffff33" highlightColor="#ffffff55" />
              <div className="flex-1">
                <Skeleton
                  height={20}
                  width="70%"
                  baseColor="#ffffff33"
                  highlightColor="#ffffff55"
                  className="mb-2"
                />
                <Skeleton height={16} width="50%" baseColor="#ffffff33" highlightColor="#ffffff55" />
              </div>
            </div>
            <div className="flex justify-between items-center">
              <div>
                <Skeleton
                  height={16}
                  width={100}
                  baseColor="#ffffff33"
                  highlightColor="#ffffff55"
                  className="mb-1"
                />
                <Skeleton height={24} width={120} baseColor="#ffffff33" highlightColor="#ffffff55" />
              </div>
              <Skeleton
                height={44}
                width={120}
                baseColor="#05d172"
                highlightColor="#06e67a"
                className="rounded-full"
              />
            </div>
          </div>

          {/* Feedback Skeleton */}
          <div className="flex flex-col gap-4 px-4 sm:px-8 py-4 w-full bg-[#f0eff0] rounded-2xl">
            <div className="flex items-center gap-4">
              <Skeleton circle width={24} height={24} />
              <Skeleton height={20} width="60%" />
            </div>
            <Skeleton height={16} width="80%" />
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <Skeleton key={star} circle width={32} height={32} />
              ))}
            </div>
            <Skeleton height={80} className="rounded-lg" />
            <div className="flex justify-end">
              <Skeleton height={40} width={120} className="rounded-full" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const AssessmentResults = ({
  recommendations,
  onStartOver,
  canCreateNew,
  onGenerateNew,
  isGenerating,
  assessmentId,
  existingFeedback,
  onFeedbackSubmitted,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const addToCart = useAddToCart();

  const userProfile = useSelector((state) => state.auth.profile);
  const clientData = useSelector((state) => state.client.payload);

  const [loadingStates, setLoadingStates] = React.useState({
    addToCart: {},
    buyNow: {},
    buyAll: false,
  });

  // Extract data from recommendations first
  const products = recommendations?.recommendations || [];
  const analysis = recommendations?.analysis || {};
  const bundle = recommendations?.bundle || {};
  const personalLearningPath = recommendations?.personal_learning_path || null;
  const mentoring = recommendations?.mentoring || null;
  
  // Determine purchase state and initial settings
  const purchaseState = getPurchaseState({
    products,
    personalLearningPath,
    mentoring
  });
  
  const [includeMentoring, setIncludeMentoring] = React.useState(
    getInitialMentoringState(purchaseState, mentoring)
  );


  // Use centralized price calculation
  const pricing = calculatePrice({
    products,
    personalLearningPath,
    mentoring,
    includeMentoring,
    bundlePricing: recommendations?.bundle_pricing
  });
  
  // Get banner configuration based on purchase state and toggle
  const bannerConfig = getBannerConfig(purchaseState, pricing, mentoring, includeMentoring);
  
  // Check if mentoring toggle should be enabled
  const toggleEnabled = shouldEnableMentoringToggle(purchaseState, mentoring);

  // Determine if all items are purchased (for conditional banner display)
  const hasAllItemsPurchased = purchaseState === 'OWNS_ALL';



  const bundleDiscount = bundle.discount || 0;

  const formatCurrency = (amount) => {
    const numAmount = Number(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      return 'Rp 0';
    }
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const handleToggleMentoring = (includeIt) => {
    // Only allow toggle if it's enabled based on purchase state
    if (toggleEnabled) {
      setIncludeMentoring(includeIt);
    }
  };

  // Update includeMentoring based on purchase state changes
  useEffect(() => {
    const newState = getInitialMentoringState(purchaseState, mentoring);
    setIncludeMentoring(newState);
  }, [purchaseState, mentoring]);

  const handleAddToCart = async (product) => {
    if (loadingStates.addToCart[product.productId]) {
      return;
    }

    setLoadingStates((prev) => ({
      ...prev,
      addToCart: { ...prev.addToCart, [product.productId]: true },
    }));

    if (!product) {
      notify.show('Tidak dapat menambahkan ke keranjang, data produk tidak lengkap', 'error');
      setLoadingStates((prev) => ({
        ...prev,
        addToCart: { ...prev.addToCart, [product.productId]: false },
      }));
      return;
    }

    const getProductType = (type) => {
      switch (type) {
        case 'course':
          return CONSTANTS.PRODUCT_TYPE.COURSE;
        case 'event':
        case 'consultation':
        case 'workshop':
          return CONSTANTS.PRODUCT_TYPE.EVENT;
        case 'mentoring':
          return CONSTANTS.PRODUCT_TYPE.MENTORING;
        default:
          return CONSTANTS.PRODUCT_TYPE.COURSE;
      }
    };

    const productType = getProductType(product.type);

    addToCart.mutate(
      {
        id: product.productId,
        name: product.title,
        price: product.price,
        originalPrice: product.originalPrice || product.price,
        discount: product.discount || 0,
        quantity: 1,
        image: product.thumbnail || product.image,
        itemType: productType.value,
        details: {
          instructor: product.instructor || '',
          category: product.category || product.type,
          slug: product.slug || '',
          source: 'assessment_recommendation',
        },
      },
      {
        onSuccess: (result) => {
          if (result && result.added === false) {
            notify.show('Produk sudah ada di keranjang', 'warning');
          } else {
            notify.show(`${product.title} berhasil ditambahkan ke keranjang`, 'success');
            const getOrderType = (productType) => {
              switch (productType.value) {
                case 'event':
                  return 'purchase_event';
                case 'mentoring':
                  return 'purchase_mentoring';
                default:
                  return 'purchase_course';
              }
            };

            const gaPayload = {
              currency: 'IDR',
              value: product.price,
              order_type: getOrderType(productType),
              page_source: 'Assessment Results',
              items: [
                {
                  item_id: product.productId,
                  item_name: product.title,
                  item_category: product.type,
                  item_category2: 'assessment_recommendation',
                  price: product.price,
                  quantity: 1,
                },
              ],
            };

            HELPERS.sendEventGA('add_to_cart', gaPayload);
            ReactPixel.track('AddToCart', {
              content_ids: [product.productId],
              content_type: product.type,
              content_name: product.title,
              currency: 'IDR',
              value: product.price,
            });
          }
        },
        onError: (error) => {
          notify.show('Gagal menambahkan ke keranjang. Silakan coba lagi.', 'error');
          console.error('Add to cart error:', error);
        },
        onSettled: () => {
          setLoadingStates((prev) => ({
            ...prev,
            addToCart: { ...prev.addToCart, [product.productId]: false },
          }));
        },
      }
    );
  };

  const handleBuy = async (product) => {
    if (loadingStates.buyNow[product.productId]) {
      return;
    }

    setLoadingStates((prev) => ({
      ...prev,
      buyNow: { ...prev.buyNow, [product.productId]: true },
    }));

    try {
      dispatch(ACTION_PAYMENT.resetCheckoutState());
      sessionStorage.removeItem('CART_PAYMENT_DATA');

      const getProductType = (type) => {
        switch (type) {
          case 'course':
            return CONSTANTS.PRODUCT_TYPE.COURSE;
          case 'event':
          case 'consultation':
          case 'workshop':
            return CONSTANTS.PRODUCT_TYPE.EVENT;
          case 'mentoring':
            return CONSTANTS.PRODUCT_TYPE.MENTORING;
          default:
            return CONSTANTS.PRODUCT_TYPE.COURSE;
        }
      };

      const productType = getProductType(product.type);

      const gaPayload = {
        currency: 'IDR',
        value: product.price,
        order_type:
          productType.value === 'event'
            ? 'purchase_event'
            : productType.value === 'mentoring'
              ? 'purchase_mentoring'
              : 'purchase_course',
        page_source: 'Assessment Results',
        items: [
          {
            item_id: product.productId,
            item_name: product.title,
            item_category: product.type,
            item_category2: 'assessment_recommendation',
            price: product.price,
            quantity: 1,
          },
        ],
      };

      HELPERS.sendEventGA('add_to_cart', gaPayload);
      ReactPixel.track('InitiateCheckout', {
        content_name: product.title,
        content_type: product.type,
        content_ids: [product.productId],
        content_category: product.type,
        value: product.price,
        currency: 'IDR',
      });

      const paymentData = {
        uuid: product.productId,
        title: product.title,
        productType: {
          id: productType.id,
          value: productType.value,
        },
        price: product.price,
        originalPrice: product.originalPrice,
        discount: product.discount,
        itemType: product.type,
        source: 'assessment_recommendation',
      };

      setLoadingStates((prev) => ({
        ...prev,
        buyNow: { ...prev.buyNow, [product.productId]: false },
      }));

      history.push(PATH.PAYMENT, paymentData);
    } catch (error) {
      notify.show('Gagal memproses pembelian. Silakan coba lagi.', 'error');
      console.error('Buy now error:', error);
      setLoadingStates((prev) => ({
        ...prev,
        buyNow: { ...prev.buyNow, [product.productId]: false },
      }));
    }
  };

  const handleBuyAction = async (action) => {
    if (loadingStates.buyAll) {
      return;
    }

    const unpurchasedProducts = pricing.unpurchasedProducts;

    if (unpurchasedProducts.length === 0 && (!includeMentoring || !mentoring?.available)) {
      notify.show('Tidak ada produk yang belum dibeli.', 'info');
      return;
    }

    setLoadingStates((prev) => ({ ...prev, buyAll: true }));

    try {
      sessionStorage.removeItem('CART_PAYMENT_DATA');

      const gaPayload = {
        currency: 'IDR',
        value: pricing.finalPrice,
        order_type: includeMentoring
          ? 'assessment_bundle_with_mentoring_checkout'
          : 'assessment_learning_path_checkout',
        page_source: 'Assessment Results',
        items: unpurchasedProducts.map((product) => ({
          item_id: product.productId,
          item_name: product.title,
          item_category: product.type,
          item_category2: 'assessment_recommendation',
          price: product.price,
          original_price: product.originalPrice,
          quantity: 1,
          discount: product.originalPrice - product.price || 0,
        })),
      };

      HELPERS.sendEventGA('add_to_cart', gaPayload);
      ReactPixel.track('InitiateCheckout', {
        content_ids: unpurchasedProducts.map((product) => product.productId),
        content_type: 'product',
        num_items: unpurchasedProducts.length,
        currency: 'IDR',
        value: pricing.finalPrice,
        contents: unpurchasedProducts.map((product) => ({
          id: product.productId,
          quantity: 1,
          item_price: product.price,
        })),
      });

      if (!includeMentoring) {


        if (personalLearningPath && personalLearningPath.id) {
          const bundlePricing = recommendations?.bundle_pricing;
          const lpData = bundlePricing?.learning_path;

          const apiOriginalPrice = lpData?.list_price || personalLearningPath.pricing?.original_price || 0;
          const apiSellingPrice = lpData?.selling_price || personalLearningPath.pricing?.remaining_amount || 0;

          const learningProgramData = {
            title: personalLearningPath.title,
            productType: CONSTANTS.PRODUCT_TYPE.LEARNING_PROGRAM,
            price: apiSellingPrice,
            originalPrice: apiOriginalPrice,
            uuid: personalLearningPath.id,
            slug: personalLearningPath.slug,
            source: 'assessment_personal_learning_path',
            includeMentoring: false,
            mentoringItems: [],
          };

          setLoadingStates((prev) => ({ ...prev, buyAll: false }));
          history.push(PATH.PAYMENT, learningProgramData);
        } else {
          notify.show('Learning path tidak tersedia. Silakan coba lagi.', 'error');
          setLoadingStates((prev) => ({ ...prev, buyAll: false }));
        }
      } else {

        const cartItems = [];

        if (personalLearningPath && personalLearningPath.id) {
          const bundlePricing = recommendations?.bundle_pricing;
          const bundleData = bundlePricing?.bundle;
          const lpBreakdown = bundleData?.breakdown?.learning_path;

          const originalPrice = bundlePricing?.learning_path?.list_price || personalLearningPath.pricing?.original_price || 0;
          const bundlePrice = lpBreakdown?.bundle_price || bundlePricing?.learning_path?.selling_price || 0;

          if (originalPrice && bundlePrice) {
            cartItems.push({
              id: personalLearningPath.id,
              name: personalLearningPath.title,
              price: bundlePrice,
              originalPrice: originalPrice,
              discount: ((originalPrice - bundlePrice) / originalPrice) * 100, // percentage
              itemType: 'learning_program',
              quantity: 1,
              image: personalLearningPath.image || null,
              details: {
                instructor: '',
                category: 'Learning Program',
                slug: personalLearningPath.slug || '',
                source: 'assessment_recommendation',
              },
            });
          }
        }

        if (mentoring?.available && mentoring?.recommendations?.length > 0) {
          mentoring.recommendations.forEach((mentoringItem) => {
            if (!mentoringItem.is_purchased && !mentoringItem.is_owned && mentoringItem.can_purchase) {
              // Use bundle_pricing API data directly for bundle mode
              const bundlePricing = recommendations?.bundle_pricing;
              const mentoringPricing = bundlePricing?.mentoring;
              const bundleData = bundlePricing?.bundle;
              const mentoringBreakdown = bundleData?.breakdown?.mentoring;

              let originalPrice, bundlePrice;

              if (mentoringPricing && mentoringPricing.uuid === mentoringItem.id) {
                // Use API data for bundle mode
                originalPrice = mentoringPricing.list_price;
                bundlePrice = mentoringBreakdown?.bundle_price || mentoringPricing.selling_price;
              } else {
                // Fallback
                originalPrice = mentoringItem.originalPrice || mentoringItem.price;
                bundlePrice = mentoringItem.price || originalPrice;
              }

              if (originalPrice && bundlePrice) {
                cartItems.push({
                  id: mentoringItem.id,
                  name: mentoringItem.title,
                  price: bundlePrice,
                  originalPrice: originalPrice,
                  discount: ((originalPrice - bundlePrice) / originalPrice) * 100, // percentage
                  itemType: 'mentoring',
                  quantity: 1,
                  image: mentoringItem.image || null,
                  details: {
                    instructor: mentoringItem.mentor || '',
                    category: 'Mentoring',
                    slug: mentoringItem.topic || '',
                    source: 'assessment_recommendation',
                  },
                });
              }
            }
          });
        }

        const bulkPaymentData = {
          cartItems: cartItems,
          voucherCode: null,
          appliedVoucherDetails: null,
          totalPrice: pricing.finalPrice,
          originalPrice: pricing.originalPrice,
          discount: pricing.totalSavings,
          source: action === 'buyMentoring' ? 'assessment_mentoring_only' : 'assessment_bundle_with_mentoring',
        };

        const CART_STORAGE_KEY = 'CART_PAYMENT_DATA';
        sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(bulkPaymentData));

        setLoadingStates((prev) => ({ ...prev, buyAll: false }));
        history.push(PATH.PAYMENT);
      }
    } catch (error) {
      notify.show('Gagal memproses pembelian. Silakan coba lagi.', 'error');
      console.error('Buy all error:', error);
      setLoadingStates((prev) => ({ ...prev, buyAll: false }));
    }
  };

  const handleSearchCatalog = () => {
    history.push(PATH.CATALOG);
  };

  const handleNavigateToCourse = (product) => {
    if (product.slug) {
      history.push(`${PATH.COURSE}/${product.slug}`);
    } else {
      history.push(`${PATH.COURSE}/${product.productId}`);
    }
  };

  const [selectedCourse, setSelectedCourse] = React.useState(null);
  const [showCourseModal, setShowCourseModal] = React.useState(false);

  const handleShowCourseDetail = (product) => {
    setSelectedCourse(product);
    setShowCourseModal(true);
  };

  const handleCloseCourseModal = () => {
    setShowCourseModal(false);
    setSelectedCourse(null);
  };

  const handleContactMentor = (mentoring) => {
    const userEmail = userProfile?.email || 'email belum tersedia';
    const mentoringTitle = mentoring.title || 'mentoring';

    const message = `Halo saya sudah membeli sesi 1-on-1 mentoring ${mentoringTitle} dengan akun ${userEmail}. Minta tolong dijadwalkan sesinya ya. Terima kasih`;

    const adminPhone = clientData?.contact_phone || '628123456789';
    const whatsappURL = `https://wa.me/${HELPERS.parsePhone(adminPhone)}?text=${encodeURIComponent(message)}`;

    window.open(whatsappURL, '_blank');
  };


  // Check if we have valid data to display
  const hasValidRecommendations = recommendations &&
    (recommendations.recommendations?.length > 0 ||
      recommendations.personal_learning_path ||
      recommendations.mentoring);

  if (!hasValidRecommendations) {
    return <AssessmentResultsSkeleton />;
  }

  return (
    <div className="flex flex-col w-full bg-[#f9f8fa] pb-6 md:pb-12">
      <section className="flex flex-col w-full max-w-[894px] items-center justify-center gap-6 sm:gap-10 px-4 md:px-16 py-8 sm:py-12 mx-auto bg-white rounded-lg shadow-sm my-4 sm:my-8">
        <div className="flex flex-col items-center gap-6 sm:gap-10 w-full">
          <AssessmentBanner
            canCreateNew={canCreateNew}
            onGenerateNew={onGenerateNew}
            isGenerating={isGenerating}
          />

          <div className="flex flex-col items-center justify-center gap-2 w-full">
            <h2 className="w-full [font-family:'Raleway',Helvetica] font-bold text-[#8553d8] text-xl sm:text-2xl text-center leading-7 sm:leading-8">
              {personalLearningPath?.title || 'Program Pembelajaranmu'}
            </h2>
            <p className="w-full [font-family:'Inter',Helvetica] font-normal text-[#231838] text-sm sm:text-base leading-5 sm:leading-6 text-center max-w-2xl break-words">
              {personalLearningPath?.description || analysis.summary ||
                'Kami merancang kumpulan kursus dan program konsultasi yang sesuai dengan kebutuhanmu. Yuk, lanjutkan perjalanan belajarmu dan raih potensi terbaik!'}
            </p>
          </div>
        </div>

        <div className="flex flex-col items-start gap-6 sm:gap-8 w-full">
          {products.length > 0 || (mentoring?.available && mentoring?.recommendations?.length > 0) ? (
            <div className="flex flex-col items-start gap-4 sm:gap-6 w-full">
              {products.map((product, index) => (
                <ProductCard
                  key={product.productId || index}
                  product={product}
                  isLast={
                    index === products.length - 1 && (!mentoring?.available || !mentoring?.recommendations?.length)
                  }
                  onAddToCart={handleAddToCart}
                  onBuy={handleBuy}
                  loadingStates={loadingStates}
                  onNavigateToCourse={handleNavigateToCourse}
                  onShowCourseDetail={handleShowCourseDetail}
                />
              ))}

              {mentoring?.available &&
                mentoring?.recommendations?.map((mentoringItem, index) => {
                  // Use bundle_pricing data directly from API
                  const bundlePricing = recommendations?.bundle_pricing;
                  const mentoringPricing = bundlePricing?.mentoring;

                  let originalPrice, price, discount;

                  // Check if mentoring should use bundle pricing
                  const shouldUseBundlePrice = includeMentoring && 
                    purchaseState !== 'OWNS_LP_ONLY' && 
                    purchaseState !== 'HAS_SUBSCRIPTION_ACCESS' &&
                    bundlePricing?.bundle?.breakdown?.mentoring;

                  if (shouldUseBundlePrice) {
                    // Use bundle price for mentoring
                    const bundleData = bundlePricing.bundle;
                    const mentoringBreakdown = bundleData.breakdown.mentoring;
                    
                    originalPrice = mentoringPricing?.list_price || mentoringItem.price;
                    price = mentoringBreakdown.bundle_price || mentoringBreakdown.final_price;
                    const bundleDiscount = mentoringBreakdown.bundle_discount || 0;
                    discount = bundleDiscount > 0 ? Math.round((bundleDiscount / originalPrice) * 100) : undefined;
                  } else if (mentoringPricing && mentoringPricing.uuid === mentoringItem.id) {
                    // Use individual pricing
                    originalPrice = mentoringPricing.list_price;
                    price = mentoringPricing.selling_price;
                    const totalSavings = mentoringPricing.total_savings ||
                      (mentoringPricing.direct_discount + mentoringPricing.extra_discount);
                    discount = totalSavings > 0 ? Math.round((totalSavings / originalPrice) * 100) : undefined;
                  } else {
                    // Fallback for mentoring items not in bundle_pricing
                    originalPrice = mentoringItem.originalPrice || mentoringItem.price;
                    price = mentoringItem.price || originalPrice;
                    discount = Number(mentoringItem.discount) > 0 ? mentoringItem.discount : undefined;
                  }

                  const mentoringWithPrice = {
                    ...mentoringItem,
                    price: price,
                    originalPrice: originalPrice,
                    discount: discount,
                    duration_minutes: mentoringItem.duration_minutes || 60,
                    is_purchasable: mentoringItem.is_purchasable !== false,
                    // Add bundle context
                    isInBundle: shouldUseBundlePrice,
                    bundleContext: shouldUseBundlePrice ? 'with_learning_path' : 'individual'
                  };

                  return (
                    <MentoringCard
                      key={mentoringItem.id || `mentoring-${index}`}
                      mentoring={mentoringWithPrice}
                      isLast={index === mentoring.recommendations.length - 1}
                      onContact={handleContactMentor}
                      onAddToCart={handleAddToCart}
                      onBuy={handleBuy}
                      loadingStates={loadingStates}
                    />
                  );
                })}
            </div>
          ) : (
            <div className="text-center py-8 w-full">
              <p className="text-[#636363]">Tidak ada rekomendasi yang tersedia saat ini.</p>
            </div>
          )}

          {/* Purchase Banner - State-driven approach */}
          <PurchaseBanner
            bannerConfig={bannerConfig}
            pricing={pricing}
            includeMentoring={includeMentoring}
            onToggleMentoring={handleToggleMentoring}
            onBuyAction={handleBuyAction}
            isLoading={loadingStates.buyAll}
            showToggle={bannerConfig.showToggle && toggleEnabled}
            toggleDisabled={!toggleEnabled || loadingStates.buyAll}
          />

          <FeedbackComponent
            assessmentId={assessmentId}
            existingFeedback={existingFeedback}
            onFeedbackSubmitted={onFeedbackSubmitted}
          />
        </div>
      </section>

      {selectedCourse && (
        <CourseDetailModal
          isOpen={showCourseModal}
          onClose={handleCloseCourseModal}
          courseSlug={selectedCourse.slug || selectedCourse.productId}
          courseUuid={selectedCourse.productId}
        />
      )}
    </div>
  );
};

export { AssessmentResultsSkeleton };
export default AssessmentResults;
